import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { useState,useEffect,useCallback } from "react"
import { requireCapability } from '@/lib/auth'
import { createFileRoute,Outlet,useNavigate,useSearch } from "@tanstack/react-router"
import { z } from "zod"
import { api } from '@/lib/api-client'
import { useOrganizationShop } from "@/hooks/use-organization-shop"
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from "@/components/ui/table"
import {
Tooltip,
TooltipContent,
TooltipProvider,
TooltipTrigger,
} from "@/components/ui/tooltip"
import { Badge } from "@/components/ui/badge"
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search,X,RefreshCw } from "lucide-react"
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { AdjustmentSidePanel } from "@/features/inventory/adjustment-side-panel"
import { PageBreadcrumb } from "@/components/dashboard/page-breadcrumb"
import { InventoryEmptyState } from "@/components/dashboard/inventory-empty-state"
import { subDays,startOfDay,endOfDay } from 'date-fns'
import { cn } from "@/lib/utils"
import { useQuery } from "@tanstack/react-query"
import { queryKeys } from "@/lib/query-keys"
import { extractArray,extractMeta } from "@/features/http"
import { formatInStoreTz } from '@/lib/time'

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(20),
  q: z.string().catch(''),
  track: z.string().optional(),
  type: z.string().optional(),
  dateRange: z.string().optional(),
})

type AdjustmentsSearch = z.infer<typeof searchSchema>

interface Adjustment {
  id?: string
  Id?: string
  track?: string
  packageId?: string
  adjustmentType?: string
  type?: string
  reason?: string
  Reason?: string
  quantityChange?: string | number
  quantity?: string | number
  Quantity?: string | number
  uom?: string
  unitOfMeasureName?: string
  createdAt?: string
  adjustmentDate?: string
  itemName?: string
  itemLabel?: string
  productName?: string
  Label?: string
  quantityBefore?: string | number
  previousQuantity?: string | number
  quantityAfter?: string | number
  newQuantity?: string | number
  sourceLabel?: string
}

export const Route = createFileRoute('/_dashboard/inventory/adjustments')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: searchSchema,
  component: AdjustmentsLayout,
})

let suppressedAdjustmentsAutoOpenSignature: string | null = null

function AdjustmentsLayout() {
  return <Outlet />
}

export function AdjustmentsPage({ selectedId }: { selectedId: string | undefined }) {
  const { shopId, shop, isLoading: shopLoading } = useOrganizationShop()
  const navigate = useNavigate()
  const {
    page: currentPage,
    limit,
    q: searchQuery,
    track: urlTrack = 'all',
    type: typeFilter = 'all',
    dateRange: dateRangeFilter = 'all',
  } = useSearch({ from: Route.id }) as AdjustmentsSearch & { track: string; type: string; dateRange: string }
  // A store without Metrc has only local adjustments — the whole Metrc/local
  // distinction is noise there, so the Track filter/column/badges are hidden
  // and any stale ?track= URL value is ignored (mirrors the Stock page).
  const isMetrcEnabled = shop?.isMetrcEnabled ?? true
  const trackFilter = isMetrcEnabled ? urlTrack : 'all'
  const pageSignature = `${shopId ?? 'no-shop'}:${currentPage}:${limit}:${searchQuery}:${trackFilter}:${typeFilter}:${dateRangeFilter}`

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const buildDateParams = () => {
    if (!dateRangeFilter || dateRangeFilter === "all") return {}
    const today = new Date()
    const dateTo = endOfDay(today).toISOString()
    let dateFrom = ""
    if (dateRangeFilter === "today") dateFrom = startOfDay(today).toISOString()
    else if (dateRangeFilter === "7d") dateFrom = startOfDay(subDays(today, 7)).toISOString()
    else if (dateRangeFilter === "30d") dateFrom = startOfDay(subDays(today, 30)).toISOString()
    return dateFrom ? { dateFrom, dateTo } : {}
  }

  const { data: response, isLoading: queryLoading, refetch } = useQuery({
    queryKey: queryKeys.adjustments(shopId || '', {
      page: currentPage,
      limit,
      search: debouncedSearch,
      track: trackFilter,
      type: typeFilter,
      dateRange: dateRangeFilter,
    }),
    queryFn: async () => {
      const params: Record<string, string | number> = { shopId: shopId!, page: currentPage, limit }
      if (debouncedSearch) params.search = debouncedSearch
      if (trackFilter && trackFilter !== "all") params.track = trackFilter
      if (typeFilter && typeFilter !== "all") params.type = typeFilter
      const dateParams = buildDateParams()
      Object.assign(params, dateParams)

      const res = await api.get(`/inventory/adjustments`, { params })
      return res
    },
    enabled: !!shopId,
  })

  const adjustments = extractArray<Adjustment>(response)
  const { total: totalItems, totalPages } = extractMeta(response)
  const loading = queryLoading || shopLoading
  const currentPageHasSelectedAdjustment =
    !!selectedId && adjustments.some((adjustment) => (adjustment.id || adjustment.Id) === selectedId)
  const effectiveSelectedAdjustmentId = (() => {
    if (currentPageHasSelectedAdjustment) return selectedId
    if (loading || adjustments.length === 0) return undefined
    if (!selectedId && suppressedAdjustmentsAutoOpenSignature === pageSignature) return undefined
    return adjustments[0].id || adjustments[0].Id
  })()

  const selectedAdjustment = adjustments.find(a => (a.id || a.Id) === effectiveSelectedAdjustmentId) || null

  const setSelectedAdjustmentId = useCallback((id: string | null) => {
    if (id) {
      navigate({
        to: '/inventory/adjustments/$item',
        params: { item: id },
        search: (prev) => ({ ...prev }),
      })
      return
    }

    navigate({ to: '/inventory/adjustments', search: (prev) => ({ ...prev }) })
  }, [navigate])

  const closeSelectedAdjustment = useCallback(() => {
    suppressedAdjustmentsAutoOpenSignature = pageSignature
    setSelectedAdjustmentId(null)
  }, [pageSignature, setSelectedAdjustmentId])

  useEffect(() => {
    if (!effectiveSelectedAdjustmentId || effectiveSelectedAdjustmentId === selectedId) return
    setSelectedAdjustmentId(effectiveSelectedAdjustmentId)
  }, [effectiveSelectedAdjustmentId, selectedId, setSelectedAdjustmentId])

  useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/inventory/adjustments')) {
          suppressedAdjustmentsAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const handleSearchChange = (val: string) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, q: val || undefined, page: 1 }), replace: true })
  }

  const handleTrackFilter = (val: string) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, track: val === "all" ? undefined : val, page: 1 }) })
  }

  const handleTypeFilter = (val: string) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, type: val === "all" ? undefined : val, page: 1 }) })
  }

  const handleDateRangeFilter = (val: string) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, dateRange: val === "all" ? undefined : val, page: 1 }) })
  }

  const handlePageChange = (newPage: number) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, page: newPage }) })
  }

  const handleLimitChange = (newLimit: number) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, limit: newLimit, page: 1 }) })
  }

  return (
    <div className="space-y-6 h-full">
      <PageBreadcrumb />
      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Inventory Adjustments</h2>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            {isMetrcEnabled
              ? 'Full audit trail across Metrc and local inventory changes.'
              : 'Full audit trail of inventory changes.'}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative max-w-sm flex-1 group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by item..."
            className="pl-9 pr-8"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleSearchChange('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 self-start"
          title="Refresh"
          disabled={isRefreshing}
          onClick={() => {
            setIsRefreshing(true)
            void refetch()
            setTimeout(() => setIsRefreshing(false), 600)
          }}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
        <div className="flex flex-wrap gap-2">
          {isMetrcEnabled && (
            <Select value={trackFilter} onValueChange={handleTrackFilter}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Track" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tracks</SelectItem>
                <SelectItem value="metrc">Metrc</SelectItem>
                <SelectItem value="local">Local</SelectItem>
              </SelectContent>
            </Select>
          )}

          <Select value={typeFilter} onValueChange={handleTypeFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="SALE">Sale</SelectItem>
              <SelectItem value="RECEIVING">Receiving</SelectItem>
              <SelectItem value="WASTE">Waste</SelectItem>
              <SelectItem value="CORRECTION">Correction</SelectItem>
              <SelectItem value="RECOUNT">Recount</SelectItem>
              <SelectItem value="RETURN">Return</SelectItem>
              <SelectItem value="TRANSFER_OUT">Transfer</SelectItem>
              <SelectItem value="SAMPLE">Sample</SelectItem>
              <SelectItem value="PROMOTION">Promotion</SelectItem>
              <SelectItem value="OTHER">Other</SelectItem>
            </SelectContent>
          </Select>

          <Select value={dateRangeFilter} onValueChange={handleDateRangeFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!loading && adjustments.length === 0 ? "" : "border rounded-md squircle bg-card shadow-sm overflow-hidden"} ${selectedAdjustment ? "w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]" : "w-full"}`}>
          {!loading && adjustments.length === 0 ? (
            <InventoryEmptyState
              title="No adjustments found"
              description={
                isMetrcEnabled
                  ? 'Adjustments will appear here once inventory changes are recorded or synced from Metrc.'
                  : 'Adjustments will appear here once inventory changes are recorded.'
              }
            />
          ) : (
            <>
              <div className="overflow-auto max-h-[calc(100vh-14rem)]">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[160px] pl-4">Date & Time</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className={`w-[140px] ${selectedAdjustment ? "hidden" : ""}`}>Type</TableHead>
                      <TableHead className="w-[120px] text-right">Qty Change</TableHead>
                      <TableHead className={`w-[160px] text-center ${selectedAdjustment ? "hidden" : "hidden md:table-cell"}`}>Before &rarr; After</TableHead>
                      {isMetrcEnabled && <TableHead className="w-[100px] rounded-tr-none border-r-0">Track</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell className="pl-4"><div className="h-4 w-28 rounded bg-muted" /></TableCell>
                          <TableCell><div className="space-y-2"><div className="h-4 w-32 rounded bg-muted" /><div className="h-3 w-20 rounded bg-muted" /></div></TableCell>
                          <TableCell className={selectedAdjustment ? "hidden" : ""}><div className="h-4 w-20 rounded bg-muted" /></TableCell>
                          <TableCell className="text-right"><div className="h-4 w-12 ml-auto rounded bg-muted" /></TableCell>
                          <TableCell className={selectedAdjustment ? "hidden" : "hidden md:table-cell"}><div className="h-4 w-24 mx-auto rounded bg-muted" /></TableCell>
                          {isMetrcEnabled && <TableCell className="rounded-tr-none border-r-0"><div className="h-6 w-16 rounded-full bg-muted" /></TableCell>}
                        </TableRow>
                      ))
                    ) : (
                      adjustments.map((adj) => {
                        const id = adj.id || adj.Id || ''
                        const isMetrc = adj.track?.toLowerCase() === 'metrc' || !!adj.packageId
                        const trackBadge = isMetrc ? 'Metrc' : 'Local'
                        const typeText = adj.adjustmentType || adj.type || adj.reason || adj.Reason || "Adjustment"
                        const qtyChange = parseFloat(String(adj.quantityChange || adj.quantity || adj.Quantity || "0"))
                        const isPositive = qtyChange > 0
                        const uom = adj.uom || adj.unitOfMeasureName || "ea"
                        const dateStr = adj.createdAt || adj.adjustmentDate || new Date().toISOString()
                        const itemName = adj.itemName || adj.itemLabel || adj.productName || adj.Label || "Unknown"
                        const beforeQty = adj.quantityBefore !== undefined ? parseFloat(String(adj.quantityBefore)) : (adj.previousQuantity !== undefined ? parseFloat(String(adj.previousQuantity)) : null)
                        const afterQty = adj.quantityAfter !== undefined ? parseFloat(String(adj.quantityAfter)) : (adj.newQuantity !== undefined ? parseFloat(String(adj.newQuantity)) : null)

                        return (
                          <TableRow
                            key={id}
                            className={cn(
                              "cursor-pointer hover:bg-muted/50 transition-colors border-b",
                              effectiveSelectedAdjustmentId === id && "bg-muted/80"
                            )}
                            onClick={() => {
                              if (effectiveSelectedAdjustmentId === id) {
                                closeSelectedAdjustment()
                                return
                              }
                              setSelectedAdjustmentId(id)
                            }}
                          >
                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground pl-4">
                              {formatInStoreTz(dateStr, "MMM dd, yyyy h:mma")}
                            </TableCell>
                            <TableCell className="w-full pr-4">
                              <TooltipProvider delayDuration={400}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="font-medium truncate cursor-help">{itemName}</div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" align="start">
                                    <p className="max-w-xs break-words">{itemName}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {isMetrc && (adj.sourceLabel || adj.packageId) && (
                                <div className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                                  {adj.sourceLabel || adj.packageId}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className={cn(selectedAdjustment ? "hidden" : "")}>
                              <Badge variant="outline" className="font-normal text-[11px] py-0.5 px-2 bg-muted/30 text-muted-foreground border-border/50">
                                {(() => {
                                  if (typeText === 'SyncReconciliation_InitialSync') return 'Initial System Sync'
                                  if (typeText === 'WASTE') return 'Waste / Spoilage'
                                  if (typeText === 'RECEIVING') return 'Inventory Received'
                                  if (typeText === 'RECOUNT') return 'Physical Recount'
                                  if (typeText === 'CORRECTION') return 'Data Correction'
                                  return typeText
                                })()}
                              </Badge>
                            </TableCell>
                            <TableCell className={cn(
                              "text-right font-mono font-medium",
                              isPositive ? "text-emerald-500" : (qtyChange < 0 ? "text-red-500" : "")
                            )}>
                              {isPositive ? '+' : ''}{qtyChange} {uom}
                            </TableCell>
                            <TableCell className={cn("text-center text-muted-foreground font-mono text-sm", selectedAdjustment ? "hidden" : "hidden md:table-cell")}>
                              {beforeQty !== null && afterQty !== null ? (
                                <>{beforeQty} &rarr; {afterQty}</>
                              ) : "-"}
                            </TableCell>
                            {isMetrcEnabled && (
                              <TableCell>
                                <Badge variant={isMetrc ? "default" : "secondary"} className="font-normal text-[10px] uppercase">
                                  {trackBadge}
                                </Badge>
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              <DataTablePagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                entityName="adjustments"
                isLoading={loading}
                onPageChange={(p: number) => handlePageChange(p)}
                onLimitChange={(l: number) => handleLimitChange(l)}
              />
            </>
          )}
        </div>

        {selectedAdjustment && (
          <div className="w-full md:w-[320px] lg:w-[360px] sticky top-0 shrink-0 mt-0 flex flex-col h-fit max-h-[calc(100vh-6rem)]">
            <AdjustmentSidePanel
              open={!!selectedAdjustment}
              onOpenChange={(open: boolean) => {
                if (!open) closeSelectedAdjustment()
              }}
              adjustment={selectedAdjustment}
            />
          </div>
        )}
      </div>
    </div>
  )
}
